import { FC, useContext, useEffect, useMemo, useState } from 'react';

import { useLazyQuery, useQuery } from '@apollo/client';
import {
  Button,
  DateRangePicker,
  InfoBlock,
  Separator,
} from '@smartshell/ui-kit';
import dayjs from 'dayjs';

import { useSharedDateRange, useWindowResize } from 'hooks';
import { LocalizationContext } from 'localization';
import { formatDateForServer } from 'utils';

import { HOST_GROUPS } from '../../Bookings/schema';
import { IHostGroupsResponse } from '../../Bookings/types';
import { ChartBlock } from './ChartBlock';
import {
  EType,
  IData,
  IPlayAndApplicationReportFileResponse,
  IPlayAndApplicationReportRequest,
  IPlayAndApplicationReportResponse,
} from './interface';
import s from './PlayAndApplications.module.scss';
import {
  GENERATE_APPLICATIONS_REPORT,
  GENERATE_APPLICATIONS_REPORT_FILE,
} from './schema';
import { TableBlock } from './TableBlock';

export const PlayAndApplications: FC = () => {
  const { t, lang } = useContext(LocalizationContext);

  const {
    startDate,
    endDate,
    periodType,
    offset,
    setStartDate,
    setEndDate,
    setPeriodType,
    setOffset,
  } = useSharedDateRange();
  const [error, setError] = useState<string | null>(null);

  const [hasGenerated, setHasGenerated] = useState(false);

  const [type, setType] = useState<EType>(EType.APPLICATION);
  const [selectedHostGroup, setSelectedHostGroup] = useState<number | null>(
    null
  );
  const [selectedHost, setSelectedHost] = useState<number | null>(null);

  const { data: hostGroupsData } = useQuery<IHostGroupsResponse>(HOST_GROUPS, {
    fetchPolicy: 'no-cache',
  });

  const [generateReport, { data: response, loading, called, variables }] =
    useLazyQuery<
      IPlayAndApplicationReportResponse,
      IPlayAndApplicationReportRequest
    >(GENERATE_APPLICATIONS_REPORT, {
      fetchPolicy: 'no-cache',
      onCompleted: (response) => {
        if (!response) return;
      },
      onError: (error) => {
        setError(error.message);
      },
    });

  const [generateReportFile, { loading: reportFileLoading }] = useLazyQuery<
    IPlayAndApplicationReportFileResponse,
    IPlayAndApplicationReportRequest
  >(GENERATE_APPLICATIONS_REPORT_FILE, {
    fetchPolicy: 'no-cache',
    onCompleted: (response) => {
      if (!response) return;

      open(response.applicationsReport.url, 'self');
    },
    onError: (error) => {
      setError(error.message);
    },
  });

  useEffect(() => {
    if (hasGenerated && startDate && endDate) {
      const input: IPlayAndApplicationReportRequest['input'] = {
        from: formatDateForServer(dayjs(startDate)),
        to: formatDateForServer(dayjs(endDate)),
        type: type,
        hostGroupId: selectedHostGroup ? selectedHostGroup : undefined,
        hostId: selectedHost ? selectedHost : undefined,
      };

      generateReport({ variables: { input } });
    }
  }, [
    type,
    selectedHostGroup,
    selectedHost,
    hasGenerated,
    startDate,
    endDate,
    generateReport,
  ]);

  const data: IData[] = useMemo<Array<IData>>(() => {
    if (!response || !response.applicationsReport) {
      return [];
    }

    return response.applicationsReport.keys.map((key, index) => {
      const values = response.applicationsReport.data[index]?.values || [];
      const summaryValue =
        response.applicationsReport.summary[index]?.value || 0;
      return {
        title: key,
        numberLaunches: summaryValue,
        time: values[1],
        percent: values[2],
      };
    });
  }, [response]);
  const size = useWindowResize();

  return (
    <div className={s.wrapper}>
      {error && (
        <InfoBlock title={t('errors.error')} message={error} direction="row" />
      )}
      <div className={s.generate}>
        <DateRangePicker
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          lastValidDate={dayjs().format()}
          firstValidDate={dayjs('2022-03-01T00:00:00').format()}
          lang={lang}
          periodType={periodType}
          setPeriodType={setPeriodType}
          offset={offset}
          setOffset={setOffset}
        />
        <div className={s.buttons_block}>
          <Button
            onClick={() => {
              setError(null);
              setHasGenerated(true);

              const input: IPlayAndApplicationReportRequest['input'] = {
                from: formatDateForServer(dayjs(startDate)),
                to: formatDateForServer(dayjs(endDate)),
                type: type,
              };

              return generateReport({ variables: { input } });
            }}
            disabled={loading}
          >
            {t('reports.generate')}
          </Button>
          <Button
            variant="outline"
            onClick={() => generateReportFile({ variables })}
            disabled={!variables?.input || reportFileLoading}
            className={s.export}
            iconName="IncomeIcon"
          >
            {size.width < 1864 && size.width > 960 ? t('csv') : t('export.csv')}
          </Button>
        </div>
      </div>
      <div className={s.data}>
        <ChartBlock
          hostGroupsData={hostGroupsData}
          type={type}
          setType={setType}
          selectedHostGroup={selectedHostGroup}
          setSelectedHostGroup={setSelectedHostGroup}
          selectedHost={selectedHost}
          setSelectedHost={setSelectedHost}
        />
        {size.width < 960 && (
          <Separator style={{ margin: '0 16px', width: 'unset' }} />
        )}
        <TableBlock
          data={data}
          type={type}
          loading={loading}
          apiCalled={called}
        />
      </div>
      {size.width < 960 && (
        <InfoBlock
          className={s.mobile_info}
          title={t('mobile.placeholder')}
          iconName="ComputerIcon"
          color="blue"
        />
      )}
    </div>
  );
};
